import { Box3, Vector2, Vector2Tuple, Vector3 } from 'three';

import {
  pickSchemaFields,
  Schema,
  SchemaFieldFurniture,
  SchemaFieldNumber,
  SchemaFields,
  SchemaFieldTypesMapping,
  SchemaValues,
} from '../schema/index.js';
import { FInstanceNode } from '../types/index.js';

import { FEnsembleBase } from './FEnsembleBase.js';

const bbox = new Box3();
const vec3 = new Vector3();

export interface FEnsembleBoardroomSchemaFields extends SchemaFields {
  chairType: SchemaFieldFurniture;
  tableType: SchemaFieldFurniture;
  noOfChairs: SchemaFieldNumber;
  chairsGap: SchemaFieldNumber;
  chairDistanceFromTable: SchemaFieldNumber;
}

export interface FEnsembleBoardroomSchema extends Schema {
  fields: FEnsembleBoardroomSchemaFields;
}

export interface FEnsembleBoardroomValues {
  tableSize: SchemaFieldTypesMapping['vec2'];
  chairSize: SchemaFieldTypesMapping['vec2'];
}

export type FEnsembleBoardroomAttributes = Partial<SchemaValues<FEnsembleBoardroomSchemaFields>> &
  FEnsembleBoardroomValues;

export class FEnsembleBoardroom extends FEnsembleBase<
  FEnsembleBoardroomSchema,
  FEnsembleBoardroomValues
> {
  constructor(
    schema: FEnsembleBoardroomSchema,
    initialValues?: Partial<FEnsembleBoardroomAttributes>
  ) {
    super(
      {
        ...schema,
        fields: pickSchemaFields(schema, [
          'chairType',
          'tableType',
          'noOfChairs',
          'chairsGap',
          'chairDistanceFromTable',
        ]) as any,
      },
      {
        tableSize: [0, 0],
        chairSize: [0, 0],
        ...initialValues,
      }
    );
    this.setValue('getMaxValue', this.getMaxValue);
  }

  getNodeTypes() {
    const { chairType, tableType } = this.getValues();

    if (!chairType) {
      throw new Error('chairType is required');
    }

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [chairType, tableType];
  }

  updateObjectMeasurements() {
    const [chair, table] = this.getObjects();

    if (!chair) {
      throw new Error('chair table object');
    }

    if (!table) {
      throw new Error('missing table object');
    }

    const tableBoundingBox = bbox.setFromObject(table);
    tableBoundingBox.getSize(vec3);
    const tableSize = new Vector2(vec3.x, vec3.z);

    const chairBoundingBox = bbox.setFromObject(chair);
    chairBoundingBox.getSize(vec3);
    const chairSize = new Vector2(vec3.x, vec3.z);

    this.updateValues({
      tableSize: tableSize.toArray(),
      chairSize: chairSize.toArray(),
    });
  }

  getMaxValue = (
    tableSize: Vector2Tuple,
    chairSize: Vector2Tuple,
    chairDistanceFromTable: number,
    chairsGap: number
  ) => {
    let { maxChairs } = this.getTotalChairs({
      chairSize,
      tableSize,
      chairDistanceFromTable,
      chairsGap,
    });
    return maxChairs;
  };

  getTotalChairs(params: {
    chairSize: Vector2Tuple;
    tableSize: Vector2Tuple;
    chairDistanceFromTable: number;
    chairsGap: number;
  }) {
    const { chairSize, tableSize, chairDistanceFromTable, chairsGap } = params;
    const chairRadius = chairSize[1] / 2;
    const distanceFromCenter = chairDistanceFromTable * chairRadius - chairRadius; // chairDistanceFromTable is 0, chair center is at the edge of the table
    const noOfPartitionsPerTable = tableSize[0] / (chairSize[0] + chairsGap);

    const noOfChairsOnFrontSide = Math.floor(noOfPartitionsPerTable);
    const noOfChairsOnBackSide = Math.floor(noOfPartitionsPerTable);
    const noOfChairsOnLeftSide = tableSize[1] >= chairSize[0] ? 1 : 0;
    const noOfChairsOnRightSide = tableSize[1] >= chairSize[0] ? 1 : 0;

    const maxChairs =
      noOfChairsOnFrontSide + noOfChairsOnBackSide + noOfChairsOnLeftSide + noOfChairsOnRightSide;
    return {
      distanceFromCenter,
      noOfChairsOnFrontSide,
      noOfChairsOnBackSide,
      noOfChairsOnLeftSide,
      noOfChairsOnRightSide,
      maxChairs,
    };
  }

  computeNodes(): FInstanceNode[] {
    if (this.getObjects().length === 0) {
      return [];
    }

    const {
      chairType,
      tableType,
      noOfChairs,
      chairsGap,
      chairDistanceFromTable,
      tableSize,
      chairSize,
    } = this.getValues();

    const nodes: FInstanceNode[] = [];

    let {
      distanceFromCenter,
      noOfChairsOnFrontSide,
      noOfChairsOnBackSide,
      noOfChairsOnLeftSide,
      noOfChairsOnRightSide,
      maxChairs,
    } = this.getTotalChairs({ chairSize, tableSize, chairDistanceFromTable, chairsGap });

    let chairCount = this.getSafeIterations(Math.min(noOfChairs, maxChairs));

    const initialFrontChairPosition = [
      -tableSize[0] / 2 + chairSize[0] / 2,
      -(tableSize[1] / 2 + chairSize[1] / 2 + distanceFromCenter),
    ] as Vector2Tuple;
    const initialBackChairPosition = [
      -tableSize[0] / 2 + chairSize[0] / 2,
      tableSize[1] / 2 + chairSize[1] / 2 + distanceFromCenter,
    ] as Vector2Tuple;
    const initialLeftChairPosition = [
      -(tableSize[0] / 2 + chairSize[1] / 2 + distanceFromCenter),
      -tableSize[1] / 2 + chairSize[0] / 2,
    ] as Vector2Tuple;
    const initialRightChairPosition = [
      tableSize[0] / 2 + chairSize[1] / 2 + distanceFromCenter,
      -tableSize[1] / 2 + chairSize[0] / 2,
    ] as Vector2Tuple;

    // Add table
    nodes.push({
      id: tableType,
      type: 'table',
      position: [0, 0],
      rotation: 0,
    });

    let chairId = 0;
    let chairCounter = 0;
    const frontsidetotalChairsWidth =
      noOfChairsOnFrontSide * (chairSize[0] + chairsGap) - chairsGap;
    let frontsidechairOffset = (tableSize[0] - frontsidetotalChairsWidth) / 2;
    // Add front chairs
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsOnFrontSide) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...initialFrontChairPosition] as Vector2Tuple;
      chairPosition[0] += i * (chairSize[0] + chairsGap) + frontsidechairOffset;
      nodes.push({
        id: chairType,
        type: 'chair',
        position: chairPosition,
        rotation: -Math.PI,
      });
      chairId += 1;
      chairCounter += 1;
    }
    const backsidetotalChairsWidth = noOfChairsOnBackSide * (chairSize[0] + chairsGap) - chairsGap;
    let backsidechairOffset = (tableSize[0] - backsidetotalChairsWidth) / 2;
    // Add back chairs
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsOnBackSide) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...initialBackChairPosition] as Vector2Tuple;
      chairPosition[0] += i * (chairSize[0] + chairsGap) + backsidechairOffset;
      nodes.push({
        id: chairType,
        type: 'chair',
        position: chairPosition,
        rotation: 0,
      });
      chairId += 1;
      chairCounter += 1;
    }

    // Add left chair
    const leftsidetotalChairsWidth = noOfChairsOnLeftSide * (chairSize[1] + chairsGap) - chairsGap;
    let leftsidechairOffset = (tableSize[1] - leftsidetotalChairsWidth) / 2;
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsOnLeftSide) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...initialLeftChairPosition] as Vector2Tuple;
      chairPosition[1] += i * (chairSize[1] + chairsGap) + leftsidechairOffset;
      nodes.push({
        id: chairType,
        type: 'chair',
        position: chairPosition,
        rotation: -Math.PI / 2,
      });
      chairId += 1;
      chairCounter += 1;
    }

    // Add right chair
    const rightsidetotalChairsWidth =
      noOfChairsOnRightSide * (chairSize[1] + chairsGap) - chairsGap;
    let rightsidechairOffset = (tableSize[1] - rightsidetotalChairsWidth) / 2;
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsOnRightSide) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...initialRightChairPosition] as Vector2Tuple;
      chairPosition[1] += i * (chairSize[1] + chairsGap) + rightsidechairOffset;
      nodes.push({
        id: chairType,
        type: 'chair',
        position: chairPosition,
        rotation: Math.PI / 2,
      });
      chairId += 1;
      chairCounter += 1;
    }
    return nodes;
  }
}
